import * as THREE from './three'
import { Layers } from './Layers'

export const BLOOD_SPLATTER_SRC = 'asset://bloodsplatter.png'
const MAX_SPLATTERS = 200
const UP = new THREE.Vector3(0, 1, 0)
const DOWN = new THREE.Vector3(0, -1, 0)

const v1 = new THREE.Vector3()

let splatterTexturePromise = null
let splatterGeometry = null
let splatterMaterial = null
const splatterMeshes = []

function getSplatterTexture(world) {
  if (!splatterTexturePromise) {
    splatterTexturePromise = world.loader.load('texture', BLOOD_SPLATTER_SRC)
  }
  return splatterTexturePromise
}

function getGroundPoint(world, hitPosition) {
  const origin = v1.set(hitPosition.x, hitPosition.y + 0.5, hitPosition.z)
  const hitMask = Layers.environment.group | Layers.prop.group
  const hit = world.physics?.raycast(origin, DOWN, 10, hitMask)
  if (hit) {
    return { point: hit.point.clone(), normal: hit.normal.clone() }
  }
  return {
    point: new THREE.Vector3(hitPosition.x, hitPosition.y - 1.5, hitPosition.z),
    normal: UP.clone(),
  }
}

function trimSplatters(world) {
  while (splatterMeshes.length >= MAX_SPLATTERS) {
    const mesh = splatterMeshes.shift()
    world.stage.scene.remove(mesh)
  }
}

export function clearBloodSplatters(world) {
  if (!world.stage?.scene) return
  while (splatterMeshes.length) {
    world.stage.scene.remove(splatterMeshes.pop())
  }
}

function clearBloodParticles(world) {
  if (!world.stage?.scene) return
  const geometries = new Set()
  for (const player of world.entities.players.values()) {
    for (let i = player.activeParticles.length - 1; i >= 0; i--) {
      const p = player.activeParticles[i]
      if (p.material.color.getHex() !== 0xaa0000) continue
      world.stage.scene.remove(p.mesh)
      p.material.dispose()
      geometries.add(p.mesh.geometry)
      player.activeParticles.splice(i, 1)
    }
  }
  for (const geometry of geometries) {
    geometry.dispose()
  }
}

export function clearBloodEffects(world) {
  clearBloodSplatters(world)
  clearBloodParticles(world)
}

export function replayBloodSplatters(world, bloodHits) {
  if (!Array.isArray(bloodHits)) return
  for (const hit of bloodHits) {
    if (!hit?.p) continue
    spawnBloodSplatters(world, { x: hit.p[0], y: hit.p[1], z: hit.p[2] })
  }
}

export function spawnBloodEffect(world, activeParticles, hitPosition, worldDir = null) {
  spawnBloodParticles(world, activeParticles, hitPosition)
  if (worldDir) {
    spawnDirectionalBloodParticles(world, activeParticles, hitPosition, worldDir)
  }
  spawnBloodSplatters(world, hitPosition)
}

export function spawnBloodParticles(world, activeParticles, position) {
  const particleCount = 150
  const geometry = new THREE.BoxGeometry(0.03, 0.03, 0.03)

  for (let i = 0; i < particleCount; i++) {
    const material = new THREE.MeshStandardMaterial({
      color: 0xaa0000,
      emissive: 0xcc0000,
      emissiveIntensity: 2,
      opacity: 0.9,
      transparent: true,
    })

    const particle = new THREE.Mesh(geometry, material)
    particle.position.set(position.x, position.y, position.z)
    world.stage.scene.add(particle)

    const velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 4,
      Math.random() * 3 + 1,
      (Math.random() - 0.5) * 4
    )

    activeParticles.push({
      mesh: particle,
      material,
      velocity,
      lifetime: 0.6,
      elapsed: 0,
      initialEmissive: 2,
      gravity: 9.8,
    })
  }
}

/** Directional blood burst along the swing path (replaces impact sparks). */
export function spawnDirectionalBloodParticles(world, activeParticles, position, worldDir) {
  const particleCount = 42
  const geometry = new THREE.BoxGeometry(0.03, 0.03, 0.03)

  for (let i = 0; i < particleCount; i++) {
    const material = new THREE.MeshStandardMaterial({
      color: 0xaa0000,
      emissive: 0xcc0000,
      emissiveIntensity: 2.5,
      opacity: 0.95,
      transparent: true,
    })

    const particle = new THREE.Mesh(geometry, material)
    particle.position.copy(position)
    world.stage.scene.add(particle)

    const velocity = new THREE.Vector3(
      worldDir.x * (4 + Math.random() * 4) + (Math.random() - 0.5) * 2.5,
      worldDir.y * (4 + Math.random() * 4) + Math.random() * 2,
      worldDir.z * (4 + Math.random() * 4) + (Math.random() - 0.5) * 2.5
    )

    activeParticles.push({
      mesh: particle,
      material,
      velocity,
      lifetime: 0.45 + Math.random() * 0.2,
      elapsed: 0,
      initialEmissive: 2.5,
      gravity: 9.8,
    })
  }
}

export async function spawnBloodSplatters(world, hitPosition) {
  if (!world.stage?.scene || !world.loader) return

  let texture
  try {
    texture = await getSplatterTexture(world)
  } catch (err) {
    console.error('[Blood] failed to load splatter texture:', err)
    return
  }

  if (!splatterGeometry) {
    splatterGeometry = new THREE.PlaneGeometry(1, 1)
    // Bake horizontal orientation into geometry so rotation.y spins flat on the ground
    splatterGeometry.rotateX(-Math.PI / 2)
  }
  if (!splatterMaterial || splatterMaterial.map !== texture) {
    splatterMaterial?.dispose()
    splatterMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      alphaTest: 0.15,
    })
  }

  const { point } = getGroundPoint(world, hitPosition)
  const count = 3 + Math.floor(Math.random() * 3)

  for (let i = 0; i < count; i++) {
    trimSplatters(world)

    const mesh = new THREE.Mesh(splatterGeometry, splatterMaterial)
    const scale = 0.5 + Math.random() * 0.7
    const stretchX = 0.85 + Math.random() * 0.3
    const stretchZ = 0.85 + Math.random() * 0.3
    mesh.scale.set(scale * stretchX, 1, scale * stretchZ)

    const offsetX = (Math.random() - 0.5) * 0.8
    const offsetZ = (Math.random() - 0.5) * 0.8
    mesh.position.set(point.x + offsetX, point.y + 0.02, point.z + offsetZ)

    // Spin around vertical axis only — geometry is already flat on XZ
    mesh.rotation.set(0, Math.random() * Math.PI * 2, 0)

    world.stage.scene.add(mesh)
    splatterMeshes.push(mesh)
  }
}
